/**
 * Notes you can correct - thin typed client over the three note routes
 * (docs/plans/crm-report-builder-search-collab-plan.md B1.3; backend/src/routes/crmNotes.js), in the
 * layout of recruit/api.ts. noteErrorOf turns any failure into a code the UI branches on and a plain
 * sentence it shows (the server's, when it sent one).
 *
 * Creating a note is NOT here: it stays POST /api/crm/contacts/:id/notes, the composer's and the
 * call-mode scratchpad's route (plan B3.3 adds `mentions` and `reply_to_id` to it).
 */
import api from '../../services/api';
import type { Communication } from '../types';
import type {
  NoteConflict,
  NoteDeleteRequest,
  NoteDeleteResponse,
  NoteEditRequest,
  NoteEditResponse,
  NoteError,
  NoteErrorCode,
  NoteHistoryNote,
  NoteHistoryResponse,
  NoteRetraction,
} from './types';

const BASE = '/api/crm/contacts';

const notePath = (contactId: string, noteId: string): string =>
  `${BASE}/${encodeURIComponent(contactId)}/notes/${encodeURIComponent(noteId)}`;

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

const FALLBACK: Record<NoteErrorCode, string> = {
  invalid: 'That change could not be saved. Check the text and the reason, then try again.',
  not_allowed: "You can't change this note.",
  not_found: "That note wasn't found. Refresh the page and try again.",
  note_changed: 'Someone changed this note after you opened it.',
  deleted: 'This note has already been deleted.',
  not_editable: "This note can't be changed here.",
  unavailable: "That isn't available right now. Please try again in a moment.",
  failed: 'Something went wrong. Please try again.',
};

const CONFLICT_CODES: ReadonlyArray<NoteErrorCode> = ['note_changed', 'deleted', 'not_editable'];

function codeFor(status: number | null, serverCode: string | null): NoteErrorCode {
  if (status === null) return 'unavailable';
  if (status === 400) return 'invalid';
  if (status === 401 || status === 403) return 'not_allowed';
  if (status === 404) return 'not_found';
  if (status === 409) {
    const known = CONFLICT_CODES.find((c) => c === serverCode);
    return known || 'failed';
  }
  if (status === 503) return 'unavailable';
  return 'failed';
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** The 409's `current` (the note as it is now); read from the top level when the server does not nest it. */
function conflictOf(data: Record<string, any>): NoteConflict {
  const cur: Record<string, any> = data.current && typeof data.current === 'object' ? data.current : data;
  const count = typeof cur.edit_count === 'number' && Number.isFinite(cur.edit_count) ? cur.edit_count : null;
  const conflict: NoteConflict = {
    code: 'note_changed',
    current: {
      body: str(cur.body),
      edit_count: count,
      edited_at: str(cur.edited_at),
      edited_by: str(cur.edited_by),
      edited_by_name: str(cur.edited_by_name) ?? str(data.edited_by_name),
    },
  };
  const sentence = str(data.error)?.trim();
  if (sentence) conflict.error = sentence;
  return conflict;
}

/**
 * Any failed note call as { code, status, message, conflict }. The message is the server's plain
 * sentence when it sent one ("Only the author can edit a note, within 24 hours of writing it."), else
 * a fixed sentence for the code; never a raw status line or stack. `conflict` is set only for the
 * 409 note_changed, with the note as it is now (Keep theirs / Save mine).
 */
export function noteErrorOf(e: unknown): NoteError {
  const res = (e as any)?.response;
  const status = typeof res?.status === 'number' ? res.status : null;
  const data: Record<string, any> = res?.data && typeof res.data === 'object' ? res.data : {};
  const serverCode = str(data.code) ?? str(data.reason);
  const code = codeFor(status, serverCode);
  const sentence = str(data.error)?.trim();
  return {
    code,
    status,
    message: sentence || FALLBACK[code],
    conflict: code === 'note_changed' ? conflictOf(data) : null,
  };
}

// ---------------------------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------------------------

const noteOf = (v: unknown): Communication | null =>
  v && typeof v === 'object' && typeof (v as Communication).id === 'string' ? (v as Communication) : null;

const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

function retractionOf(v: unknown): NoteRetraction | null {
  if (v === 'failed') return 'failed';
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  return { medications: count(r.medications), conditions: count(r.conditions), labs: count(r.labs), facts: count(r.facts) };
}

/** The reason fields of an edit or a delete: a blank sentence and a non-count expected_edit_count are left out. */
function reasonFields(req: NoteDeleteRequest): NoteDeleteRequest {
  const out: NoteDeleteRequest = { reason_code: req.reason_code };
  const sentence = typeof req.reason_text === 'string' ? req.reason_text.trim() : '';
  if (sentence) out.reason_text = sentence;
  const expected = req.expected_edit_count;
  if (typeof expected === 'number' && Number.isInteger(expected) && expected >= 0) out.expected_edit_count = expected;
  return out;
}

// ---------------------------------------------------------------------------------------------
// The three routes (B1.3)
// ---------------------------------------------------------------------------------------------

/**
 * PATCH /contacts/:id/notes/:noteId: edit your own typed note inside 24 hours. Pass
 * expected_edit_count (the note's edit_count when the editor opened) so a teammate's change meanwhile
 * is the 409 (noteErrorOf(e).conflict). `mentions`, when given, is sent as is: an empty list names
 * nobody, so the server withdraws the unread mentions the old text made.
 */
export async function editNote(contactId: string, noteId: string, req: NoteEditRequest): Promise<NoteEditResponse> {
  const body: NoteEditRequest = { body: req.body, ...reasonFields(req) };
  if (Array.isArray(req.mentions)) body.mentions = req.mentions.filter((id) => typeof id === 'string' && id !== '');
  const { data } = await api.patch(notePath(contactId, noteId), body);
  return {
    ok: data?.ok !== false,
    note: noteOf(data?.note),
    unchanged: data?.unchanged === true,
  };
}

/**
 * POST /contacts/:id/notes/:noteId/delete: delete a typed note (the author inside 24 hours, a CRM
 * admin at any time). The reason travels in the body, never the URL. Answers the note as it is now
 * (body '', deleted_at set) and what was withdrawn from the profile.
 */
export async function deleteNote(contactId: string, noteId: string, req: NoteDeleteRequest): Promise<NoteDeleteResponse> {
  const { data } = await api.post(`${notePath(contactId, noteId)}/delete`, reasonFields(req));
  return {
    ok: data?.ok !== false,
    note: noteOf(data?.note),
    retraction: retractionOf(data?.retraction),
  };
}

/**
 * GET /contacts/:id/notes/:noteId/history: the note and its revisions, oldest first. A deleted note's
 * words come back only to a CRM admin, after the server has audited the view (a failed audit write is
 * a 503: noteErrorOf reads it as `unavailable`); anyone else gets words_hidden with every text null.
 */
export async function getNoteHistory(contactId: string, noteId: string): Promise<NoteHistoryResponse> {
  const { data } = await api.get(`${notePath(contactId, noteId)}/history`);
  return {
    note: noteOf(data?.note) as NoteHistoryNote | null,
    revisions: Array.isArray(data?.revisions) ? data.revisions : [],
    words_hidden: data?.words_hidden === true,
  };
}
